import { verifyAuthToken } from "../auth/validator";
import { AuthVerifyRequestMessageSchema, Message, CollectionRegistry } from "../types";
import { logger } from "../utils/logger";


export async function handleAuthVerifyRequest(
  data: any,
  collections: CollectionRegistry,
  sendMessage: (message: Message) => void
): Promise<void> {
  try {
    logger.debug('[AUTH_VERIFY_REQ] Parsing incoming auth verify request');
    const authRequest = AuthVerifyRequestMessageSchema.parse(data);
    const { id, payload } = authRequest;

    const token = payload.token;

    const wsId = authRequest.from.id ;

    logger.info(`[AUTH_VERIFY_REQ ${id}] Processing auth verify request from client: ${wsId}`);
    logger.debug(`[AUTH_VERIFY_REQ ${id}] Token present: ${!!token}`);
    logger.debug(`[AUTH_VERIFY_REQ ${id}] Token length: ${token ? token.length : 0} characters`);

    if(!token){
        logger.error(`[AUTH_VERIFY_REQ ${id}] Token not found in request`);
        sendDataResponse(id, {
            success: false,
            error: 'Token not found'
        }, sendMessage, wsId);
        return;
    }


    if(!wsId){
        logger.error(`[AUTH_VERIFY_REQ ${id}] WebSocket ID not found in request`);
        sendDataResponse(id, {
            success: false,
            error: 'WebSocket ID not found'
        }, sendMessage, wsId);
        return;
    }

    logger.info(`[AUTH_VERIFY_REQ ${id}] Token validation starting`);
    logger.debug(`[AUTH_VERIFY_REQ ${id}] WebSocket ID: ${wsId}`);

    // Verify token
    logger.debug(`[AUTH_VERIFY_REQ ${id}] Calling verifyAuthToken`);
    const startTime = Date.now();

    const authResult = await verifyAuthToken(token, collections);

    const verificationTime = Date.now() - startTime;
    logger.info(`[AUTH_VERIFY_REQ ${id}] Token verification completed in ${verificationTime}ms - ${authResult.success ? 'valid' : 'invalid'}`);

    if (!authResult.success) {
        logger.warn(`[AUTH_VERIFY_REQ ${id}] Token verification failed: ${authResult.error}`);
    } else {
        logger.info(`[AUTH_VERIFY_REQ ${id}] Token verified successfully for user: ${authResult.data || 'unknown'}`);
    }

    // Send response
    logger.debug(`[AUTH_VERIFY_REQ ${id}] Sending verification response to client`);
    sendDataResponse(id, authResult, sendMessage, wsId);

    logger.info(`[AUTH_VERIFY_REQ ${id}] ${authResult.success ? '✓' : '✗'} Auth verify request completed`);
    return ;
  }
  catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    const errorStack = error instanceof Error ? error.stack : undefined;

    logger.error(`[AUTH_VERIFY_REQ] Failed to handle auth verify request: ${errorMessage}`);
    logger.debug(`[AUTH_VERIFY_REQ] Error stack trace:`, errorStack);

    // Try to send error response
    try {
      const parsedData = data as any;
      if (parsedData?.id) {
        sendDataResponse(parsedData.id, {
          success: false,
          error: `Internal error: ${errorMessage}`
        }, sendMessage, parsedData.from?.id);
      }
    } catch (sendError) {
      logger.error('[AUTH_VERIFY_REQ] Failed to send error response:', sendError);
    }
  }
}

/**
 * Send a data_res response message
 */
function sendDataResponse(
  id: string,
  res: {success: boolean; error?: string; data?: any},
  sendMessage: (message: Message) => void,
  clientId?: string,
): void {
  const response: Message = {
    id,
    type: 'AUTH_VERIFY_RES',
    from: { type: 'data-agent' },
    to: {
        type: 'runtime',
        id: clientId
    },
    payload:{
        ...res,
    }
  };

  logger.debug(`[AUTH_VERIFY_RES ${id}] Sending ${res.success ? 'successful' : 'failed'} verification response to client: ${clientId}`);
  logger.debug(`[AUTH_VERIFY_RES ${id}] Response payload size: ${JSON.stringify(response).length} bytes`);

  if (res.error) {
    logger.debug(`[AUTH_VERIFY_RES ${id}] Error message: ${res.error}`);
  }

  if (res.data) {
    logger.debug(`[AUTH_VERIFY_RES ${id}] User verified: ${res.data}`);
  }

  sendMessage(response);
}   